import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { useUserDetails } from '@/hooks/useUserDetails';
import { formatSMPDate } from '@/components/screens/SMP/utils/smpHelpers';
import { ORDER_DETAIL_PATH } from '@/constants/paths';
import {
  useGetAccountOrderHistory,
  useGetAccountCancelledOrders,
  useGetAccountUpcomingOrders,
  ACCOUNT_ORDERS_PAGE_SIZE,
} from '@viome/viome-base/src/usecase/AccountOrders/AccountOrdersQuery';
import type {
  AccountOrder,
  AccountOrdersResponse,
  AccountOrderLineItem,
  AccountUpcomingOrdersResponse,
} from '@viome/viome-base/src/usecase/AccountOrders/AccountOrders.model';

export type AccountOrdersTab = 'upcoming' | 'cancelled' | 'history';

const isAccountOrdersTab = (value?: string): value is AccountOrdersTab =>
  value === 'upcoming' || value === 'cancelled' || value === 'history';

const TAB_CONTENT: Record<
  AccountOrdersTab,
  { title: string; emptyMessage: string; dateLabel: string }
> = {
  upcoming: {
    title: 'Future Orders',
    emptyMessage: 'You have no upcoming orders.',
    dateLabel: 'Order date',
  },
  cancelled: {
    title: 'Cancelled Orders',
    emptyMessage: 'You have no cancelled orders.',
    dateLabel: 'Cancelled on',
  },
  history: {
    title: 'Order History',
    emptyMessage: 'You have no past orders.',
    dateLabel: 'Order date',
  },
};

interface DisplayOrder {
  key: string;
  title: string;
  image?: string;
  date: string;
  orderNumber?: string;
  rawOrder?: AccountOrder;
}

const formatOrderDate = (date?: string | null) => {
  if (!date) return '—';
  const formatted = formatSMPDate(date);
  return isNaN(new Date(date).getTime()) ? '—' : formatted;
};

const getLineItem = (lineItems?: AccountOrderLineItem[]) => lineItems?.[0];

const OrderRow = ({
  order,
  dateLabel,
  onClick,
}: {
  order: DisplayOrder;
  dateLabel: string;
  onClick?: () => void;
}) => {
  const content = (
    <>
      {order.image ? (
        <img
          src={order.image}
          alt={order.title}
          className="size-[72px] rounded-[12px] object-cover shrink-0"
        />
      ) : (
        <div
          className="size-[72px] rounded-[12px] bg-background-secondary shrink-0"
          aria-hidden
        />
      )}

      <div className="flex flex-col gap-2 min-w-0">
        <span className="typography-body text-primary">{order.title}</span>
        <span className="typography-caption1 text-tertiary">
          {dateLabel}: {formatOrderDate(order.date)}
        </span>
        {order.orderNumber && (
          <span className="typography-caption1 text-tertiary">
            Order #{order.orderNumber}
          </span>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="flex items-center gap-5 py-5 border-b border-stroke-secondary-subtle w-full text-left cursor-pointer"
        data-testid="accountOrders-row"
        onClick={onClick}
      >
        {content}
        <ChevronRightIcon
          className="size-6 text-tertiary shrink-0 ml-auto"
          aria-hidden
        />
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-5 py-5 border-b border-stroke-secondary-subtle"
      data-testid="accountOrders-row"
    >
      {content}
    </div>
  );
};

export const AccountOrdersPage = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab: string }>();
  const tab = isAccountOrdersTab(tabParam) ? tabParam : null;

  const userDetails = useUserDetails();
  const shopifyUserId = userDetails.shopifyUserID || 0;
  // useUserDetails hydrates userID and shopifyUserID together from local
  // storage in one effect, so once userID is resolved a still-falsy
  // shopifyUserId means the account has no Shopify link, not that hydration
  // is still pending.
  const isHydrated = !!userDetails.userID || !!shopifyUserId;
  const isMissingShopifyLink = isHydrated && !shopifyUserId;

  const isUpcoming = tab === 'upcoming';
  const isHistory = tab === 'history';

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Each tab drives a separate useInfiniteQuery instance (own query key, own
  // `pages` cache) — switching `:tab` just changes which one is enabled/active,
  // no manual pagination state to reset here.
  const historyQuery = useGetAccountOrderHistory(
    shopifyUserId,
    ACCOUNT_ORDERS_PAGE_SIZE,
    { enabled: isHistory && !!shopifyUserId },
  );
  const cancelledQuery = useGetAccountCancelledOrders(
    shopifyUserId,
    ACCOUNT_ORDERS_PAGE_SIZE,
    { enabled: tab === 'cancelled' && !!shopifyUserId },
  );
  const upcomingQuery = useGetAccountUpcomingOrders(
    shopifyUserId,
    ACCOUNT_ORDERS_PAGE_SIZE,
    { enabled: isUpcoming && !!shopifyUserId },
  );

  const activeQuery = isUpcoming
    ? upcomingQuery
    : tab === 'cancelled'
      ? cancelledQuery
      : historyQuery;

  const orders = useMemo<DisplayOrder[]>(() => {
    if (isUpcoming) {
      const pages =
        (upcomingQuery.data?.pages as
          | AccountUpcomingOrdersResponse[]
          | undefined) ?? [];
      return pages.flatMap((response, pageIndex) =>
        (response.data?.future_orders ?? []).map((order, index) => {
          const lineItem = getLineItem(order.line_items);
          return {
            key: `upcoming-${pageIndex}-${index}`,
            title: lineItem?.title || 'Subscription Order',
            image: lineItem?.image,
            date: order.date,
          };
        }),
      );
    }

    const source =
      tab === 'cancelled' ? cancelledQuery.data : historyQuery.data;
    const pages = (source?.pages as AccountOrdersResponse[] | undefined) ?? [];
    return pages.flatMap((response, pageIndex) =>
      (response.data?.orders ?? []).map((order, index) => {
        const lineItem = getLineItem(order.line_items);
        return {
          key: order.shopify_order_number || `${tab}-${pageIndex}-${index}`,
          title: lineItem?.title || 'Order',
          image: lineItem?.image,
          date:
            tab === 'cancelled'
              ? (order.cancelled_at ?? order.order_date)
              : order.order_date,
          orderNumber: order.shopify_order_number,
          rawOrder: isHistory ? order : undefined,
        };
      }),
    );
  }, [
    isUpcoming,
    isHistory,
    tab,
    upcomingQuery.data,
    cancelledQuery.data,
    historyQuery.data,
  ]);

  const hasNextPage = activeQuery?.hasNextPage || false;
  const isFetchingNextPage = activeQuery.isFetchingNextPage;

  const loadNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    activeQuery.fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, activeQuery]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextPage();
        }
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, loadNextPage]);

  const openOrderDetail = useCallback(
    (order: DisplayOrder) => {
      if (!order.orderNumber || !order.rawOrder) return;
      navigate(ORDER_DETAIL_PATH.replace(':orderNumber', order.orderNumber), {
        state: { accountOrder: order.rawOrder },
      });
    },
    [navigate],
  );

  if (!tab) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="notFound"
        hideBackground
        hideNavBar
      />
    );
  }

  const { title, emptyMessage, dateLabel } = TAB_CONTENT[tab];
  const isFirstPageLoading = !isHydrated || activeQuery.isLoading;

  if (isFirstPageLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (isMissingShopifyLink || activeQuery.isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  return (
    <section
      className={`page-container flex flex-col gap-12 pb-24 ${
        orders.length > 0 ? '' : 'min-h-[calc(100svh-9rem)]'
      }`}
      data-testid={`accountOrders-page-${tab}`}
    >
      <div className="flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <h1
          className="typography-h6 text-primary pt-5"
          data-testid="accountOrders-title"
        >
          {title}
        </h1>
      </div>

      <div className={`flex flex-col ${orders.length > 0 ? '' : 'flex-1'}`}>
        {orders.length > 0 ? (
          orders.map((order) => (
            <OrderRow
              key={order.key}
              order={order}
              dateLabel={dateLabel}
              onClick={
                isHistory && order.orderNumber
                  ? () => openOrderDetail(order)
                  : undefined
              }
            />
          ))
        ) : (
          <div className="flex flex-1 items-center justify-center py-5">
            <span
              className="typography-body text-secondary text-center"
              data-testid="accountOrders-empty"
            >
              {emptyMessage}
            </span>
          </div>
        )}
      </div>

      {hasNextPage && (
        <div
          ref={loadMoreRef}
          className="flex flex-col gap-4 py-4"
          data-testid="accountOrders-infiniteScrollSentinel"
        >
          {isFetchingNextPage && (
            <>
              <Skeleton className="h-24 w-full rounded-xl" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </>
          )}
        </div>
      )}
    </section>
  );
};
